<script setup lang="ts">
const props = defineProps<{ result: Record<string, unknown> }>();

function record(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown> : {};
}
const metrics = computed(() => record(props.result.metrics));
const scope = computed(() => record(props.result.scope));
const iteration = computed(() => record(props.result.iteration));
const operations = computed(() => record(props.result.liveOperations));
const range = computed(() => Object.keys(scope.value).length ? scope.value : iteration.value);
const title = computed(() => {
    switch (scope.value.kind) {
        case 'since-launch': return 'Since latest launch / relaunch';
        case 'since-first-launch': return 'Since first launch (lifetime)';
        case 'current-messaging': return scope.value.messagingSince ? 'Since last messaging edit' : 'Since first launch (no messaging edit timestamp recorded)';
        case 'rolling': return `Last ${scope.value.days} days (rolling)`;
        case 'date-range': return 'Selected date range';
        case 'calendar-range': return 'Selected calendar dates';
        case 'current-iteration': return `Current iteration ${iteration.value.number ?? ''}`;
        case 'iteration': return `Iteration ${iteration.value.number ?? scope.value.iterationNumber}`;
        default: return iteration.value.number ? `Iteration ${iteration.value.number}` : 'Campaign performance';
    }
});
function date(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString(undefined, {
        timeZone: typeof scope.value.timezone === 'string' ? scope.value.timezone : 'UTC',
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}
const rows = computed(() => [
    { key: 'sentCount', label: 'Contacts reached', unit: 'unique contacts' },
    { key: 'sentActionsCount', label: 'Messages / actions sent', unit: 'events' },
    { key: 'initialSentCount', label: 'Initial sends', unit: 'events' },
    { key: 'followUpSentCount', label: 'Follow-up sends', unit: 'events' },
    { key: 'replyCount', label: 'Contacts replying', unit: 'unique contacts' },
    { key: 'positiveReplyCount', label: 'Contacts replying positively', unit: 'unique contacts' },
    { key: 'meetingBookedCount', label: 'Contacts with meetings booked', unit: 'unique contacts' },
    { key: 'bouncedCount', label: 'Contacts bounced / blocked', unit: 'unique contacts' },
].flatMap(row => {
    const value = metrics.value[row.key];
    return typeof value === 'number' ? [{ ...row, value: value.toLocaleString() }] : [];
}));
const rates = computed(() => [
    { key: 'replyRate', label: 'Reply activity ratio', denominator: metrics.value.sentCount, formula: 'replying contacts / reached contacts' },
    { key: 'positiveRate', label: 'Positive reply activity ratio', denominator: metrics.value.sentCount, formula: 'positive contacts / reached contacts' },
    { key: 'positiveReplyRate', label: 'Positive share of replies', denominator: metrics.value.replyCount, formula: 'positive contacts / replying contacts' },
].flatMap(row => {
    const value = metrics.value[row.key];
    return typeof value === 'number' ? [{ ...row, value: row.denominator === 0 ? 'N/A' : `${(value * 100).toFixed(1)}%` }] : [];
}));
const liveRows = computed(() => [
    { key: 'pendingApprovalCount', label: 'Awaiting review' },
    { key: 'scheduledContactCount', label: 'Scheduled for first outreach' },
].flatMap(row => {
    const value = operations.value[row.key];
    return typeof value === 'number' ? [{ ...row, value: value.toLocaleString() }] : [];
}));
</script>

<template>
    <section class="mx-3 mb-3 rounded-lg border border-slate-200 bg-white p-3 space-y-3" aria-label="Campaign performance">
        <div v-if="result.error" class="text-amber-700">{{ result.error }}</div>
        <div v-else-if="result.status === 'no-connected-sequence'" class="text-slate-500">No launched sequence performance is available.</div>
        <template v-else>
            <header>
                <h4 class="font-semibold text-slate-800">{{ result.campaignName || 'Campaign performance' }}</h4>
                <p class="text-slate-600">{{ title }}</p>
                <p v-if="date(range.from) && date(range.to)" class="text-xs text-slate-500">
                    {{ date(range.from) }} → {{ date(range.to) }} ({{ scope.timezone || 'UTC' }}; end exclusive)
                </p>
                <p v-if="date(scope.asOf)" class="text-xs text-slate-500">As of {{ date(scope.asOf) }}</p>
                <p v-if="!Object.keys(scope).length" class="text-xs text-slate-500">Saved result: scope details are limited to those recorded at the time.</p>
            </header>
            <table v-if="rows.length" class="w-full text-sm">
                <thead class="text-xs text-slate-500"><tr><th class="text-left font-normal">Performance</th><th class="text-right font-normal">Count</th><th class="text-right font-normal">Unit</th></tr></thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.key" class="border-t border-slate-100">
                        <td class="py-1.5 text-slate-700">{{ row.label }}</td><td class="text-right tabular-nums font-medium">{{ row.value }}</td><td class="text-right text-xs text-slate-500">{{ row.unit }}</td>
                    </tr>
                </tbody>
            </table>
            <div v-if="rates.length" class="space-y-1 text-xs">
                <div v-for="rate in rates" :key="rate.key" class="flex flex-wrap justify-between gap-x-3">
                    <span class="text-slate-600">{{ rate.label }} <span class="text-slate-400">({{ rate.formula }})</span></span><span class="font-medium tabular-nums">{{ rate.value }}</span>
                </div>
            </div>
            <p v-if="scope.basis === 'activity'" class="text-xs text-slate-500">Activity in this period, not a first-send cohort. Replies may follow sends before the period. One person receiving three messages is one contact and three sent events.</p>
            <div v-if="liveRows.length" class="border-t border-slate-200 pt-2">
                <h5 class="text-xs font-medium text-slate-700">Live operations — current queue, not this period's results</h5>
                <div v-for="row in liveRows" :key="row.key" class="flex justify-between text-sm mt-1"><span class="text-slate-600">{{ row.label }}</span><span class="tabular-nums">{{ row.value }} contacts</span></div>
            </div>
            <p v-if="result.variantBreakdownUnavailable" class="text-xs text-slate-500">{{ result.variantBreakdownUnavailable }}</p>
        </template>
    </section>
</template>
